import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { BookOpen, Target, Brain, Clock, CheckCircle, ArrowRight } from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: BookOpen, title: "100+ Practice Questions", desc: "Comprehensive question bank covering all FE/PE topics" },
    { icon: Target, title: "FE Handbook References", desc: "Learn exactly where to find solutions in the official handbook" },
    { icon: Brain, title: "Memory Tips", desc: "Clever tricks to remember formulas and concepts" },
    { icon: Clock, title: "Timed Mock Exams", desc: "Simulate real exam conditions with 20, 50, or 100 questions" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">LearnVault</span>
          </div>
          <button
            data-testid="header-login-btn"
            onClick={handleGoogleLogin}
            className="btn-primary flex items-center gap-2"
          >
            Sign In
            <ArrowRight size={18} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6">
        <section className="py-16 sm:py-24 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Pass Your FE/PE Exam<br />
            <span className="text-blue-600">With Confidence</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Master engineering fundamentals with step-by-step explanations, 
            handbook references, and AI-powered tutoring.
          </p>
          <button
            data-testid="hero-login-btn"
            onClick={handleGoogleLogin}
            className="btn-primary text-lg px-8 py-4 flex items-center gap-3 mx-auto"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </section>

        {/* Features */}
        <section className="py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={i} className="card text-center animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <f.icon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
              <p className="text-sm text-slate-600">{f.desc}</p>
            </div>
          ))}
        </section>

        {/* How it works */}
        <section className="py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-slate-900 mb-12">How It Works</h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Choose Your Exam", desc: "Select FE or PE and your specific discipline" },
              { step: "2", title: "Practice Questions", desc: "Work through 100+ questions with detailed explanations" },
              { step: "3", title: "Track Progress", desc: "Monitor your improvement and focus on weak areas" },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 text-center">
          <div className="card max-w-2xl mx-auto bg-gradient-to-r from-blue-600 to-blue-700 border-0 text-white">
            <h2 className="text-2xl font-bold mb-4">Ready to Start Studying?</h2>
            <p className="text-blue-100 mb-6">Join thousands of engineers who passed their exams with LearnVault</p>
            <button
              data-testid="cta-login-btn"
              onClick={handleGoogleLogin}
              className="bg-white text-blue-600 font-semibold py-3 px-8 rounded-xl hover:bg-blue-50 transition-colors"
            >
              Get Started Free
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center text-slate-500 text-sm">
          <p>&copy; 2025 LearnVault. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
